import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CrudRowsService, CrudService, ENVIRONMENT, FileUploadService, HateoasMultipleResponseObject, PageSpecification, REPOSITORY_FILE_TYPE, RepositoryFile, RepositoryFileReference, TokenStorage, UploadFile, appendSerialisedHttpParams, markAsBackgroundServiceCall, urlWithParams } from '@scisys-uim/uim-common';
import { RangerDocumentType } from 'assets-model';
import { Observable } from 'rxjs';

export type RangerDocumentPermission = 'READ' | 'UPLOAD';

@Injectable({
  providedIn: 'root'
})
export class RangerDocumentService extends CrudService<RepositoryFile, RepositoryFileReference> {

  servicePath: string = '/ranger-document';

  constructor(protected http: HttpClient,
    protected fileUploadService: FileUploadService,
    @Inject(ENVIRONMENT) protected environment: any,
    private tokenStorage: TokenStorage) {
    super(http, environment, REPOSITORY_FILE_TYPE.entityDecorator);
  }

  getBlacklistedContentTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/blacklistedContentTypes');
  }
  getBlacklistedExtensions(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/blacklistedExtensions');
  }
  getWhitelistedContentTypes(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/whitelistedContentTypes');
  }
  getWhitelistedExtensions(): Observable<string[]> {
    return this.http.get<string[]>(this.serviceBaseUrl + '/whitelistedExtensions');
  }

  getRangerPermissions(rangerId: number, docType: RangerDocumentType): Observable<RangerDocumentPermission[]> {
    var params = new HttpParams();
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    return this.http.get<RangerDocumentPermission[]>(this.serviceBaseUrl + '/getRangerDocumentPermissions/' + rangerId, {
      params: params
    });
  }

  protected uploadFile(file: UploadFile, servicePath: string, docType: RangerDocumentType): Observable<HttpEvent<RepositoryFile>> {
    var params: HttpParams = appendSerialisedHttpParams(docType, 'docType');
    return this.fileUploadService.uploadRepositoryFile(file, servicePath, params);
  }

  findRangerDocuments(rangerId: number, docType: RangerDocumentType): Observable<HateoasMultipleResponseObject<RepositoryFile>> {
    var params = new HttpParams();
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    return this.deserialiseMultipleResponseListAsHateoas(this.http.get<RepositoryFile[]>(this.serviceBaseUrl + '/findAllRangerDocuments/' + rangerId, {
      params: params
    }));
  }

  countRangerDocuments(rangerId: number, docType: RangerDocumentType): Observable<number> {
    var params = new HttpParams();
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    return this.http.get<number>(this.serviceBaseUrl + '/countAllRangerDocuments/' + rangerId, {
      params: params
    });
  }

  exportRangerDocumentByFilename(rangerId: number, docType: RangerDocumentType, file: RepositoryFile, download?: boolean): string {
    var params = appendSerialisedHttpParams(!!download, "download");
    params = appendSerialisedHttpParams(file.filePath, "filename", params);
    params = appendSerialisedHttpParams(docType, "docType", params);
    params = this.tokenStorage.appendAuthenticationHttpParams(params);
    return urlWithParams(this.serviceBaseUrl + '/exportRangerDocumentByFilename/' + rangerId, params);
  }

  exportMultipleRangerDocumentsAsZipByFileSpecification(rangerId: number, docType: RangerDocumentType): string {
    var params = appendSerialisedHttpParams(params);
    if (docType) {
      params = appendSerialisedHttpParams(docType, 'docType');
    }
    params = this.tokenStorage.appendAuthenticationHttpParams(params);
    return urlWithParams(this.serviceBaseUrl + '/exportMultipleRangerDocumentsAsZipByFileSpecification/' + rangerId, params);
  }

  uploadRangerDocument(rangerId: number, docType: RangerDocumentType, file: UploadFile): Observable<HttpEvent<RepositoryFile>> {
    return this.uploadFile(file, this.serviceBaseUrl + "/uploadRangerDocument/" + rangerId, docType);
  }

  deleteRangerDocument(file: RepositoryFile) {
    var params = appendSerialisedHttpParams(file.filePath, 'filename');
    params = appendSerialisedHttpParams(file.userAttributes.rangerDocumentType, "docType", params);
    return this.http.delete(this.serviceBaseUrl + '/deleteRangerDocument/' + file.userAttributes.rangerId, {
      params: params
    });
  }

  public get(key: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public findAll(): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public findAllPaginated(pageSpec: PageSpecification): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public existsById(id: RepositoryFileReference): Observable<boolean> { throw new Error('Not implemented'); }
  public create(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public save(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public persistAll(rows: RepositoryFile[]): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public delete(id: RepositoryFileReference): Observable<void> { throw new Error('Not implemented'); }

}

@Injectable({
  providedIn: 'root'
})
export class RangerDocumentRowsService extends CrudService<RepositoryFile, RepositoryFileReference> implements CrudRowsService<RepositoryFile, RepositoryFileReference, any> {

  servicePath: string = null;

  constructor(protected service: RangerDocumentService) {
    super(null, null, REPOSITORY_FILE_TYPE.entityDecorator);
  }

  findAllDocuments(rangerId: number, docType: RangerDocumentType): Observable<HateoasMultipleResponseObject<RepositoryFile>> {
    return this.service.findRangerDocuments(rangerId, docType);
  }

  exportDocumentByFilename(rangerId: number, docType: RangerDocumentType, file: RepositoryFile, download?: boolean) {
    return this.service.exportRangerDocumentByFilename(rangerId, docType, file, download);
  }

  exportMultipleDocumentsAsZipByFileSpecification(rangerId: number, docType: RangerDocumentType): string {
    return this.service.exportMultipleRangerDocumentsAsZipByFileSpecification(rangerId, docType);
  }

  uploadFile(rangerId: number, docType: RangerDocumentType, file: UploadFile): Observable<HttpEvent<RepositoryFile>> {
    return this.service.uploadRangerDocument(rangerId, docType, file);
  }

  deleteDocumentByFilename(file: RepositoryFile) {
    return this.service.deleteRangerDocument(file);
  }

  public findAllInGroup(key: any): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public get(key: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public findAll(): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public findAllPaginated(pageSpec: PageSpecification): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public existsById(id: RepositoryFile): Observable<boolean> { throw new Error('Not implemented'); }
  public create(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public save(item: RepositoryFile): Observable<RepositoryFile> { throw new Error('Not implemented'); }
  public persistAll(rows: RepositoryFile[]): Observable<HateoasMultipleResponseObject<RepositoryFile>> { throw new Error('Not implemented'); }
  public delete(id: RepositoryFileReference): Observable<void> { throw new Error('Not implemented'); }
}

